import type { ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import type { LeadStatusFilter } from 'app/features/leads/services/gym-finder/types';
import TextField from 'app/ui/components/atoms/text-field';
import Typography from 'app/ui/components/atoms/typography';

const LEADS_STATUS_FILTERS: readonly LeadStatusFilter[] = ['all', 'new', 'contacted', 'attended'];

type LeadsToolbarProps = {
  search: string;
  status: LeadStatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: LeadStatusFilter) => void;
};

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(6)};

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      `,
    )}
  `}
`;

const SearchWrapper = styled.div`
  width: 100%;

  > div {
    min-width: 0;
    margin: 0;
    width: 100%;
  }

  input {
    width: 100%;
    height: ${({ theme: { spacing } }) => spacing(11)};
    margin: 0;
    border-color: ${({ theme }) => theme.semantic.neutrals_300};
    background: transparent;
    color: ${({ theme }) => theme.semantic.text_small_elements};
    font-size: ${({ theme: { typography } }) => typography.tiny};
    padding: ${({ theme: { spacing } }) => `${spacing(3)} ${spacing(2.5)} ${spacing(3)} ${spacing(12)}`};
    border-radius: ${({ theme: { spacing } }) => spacing(1)};

    &::placeholder {
      color: ${({ theme }) => theme.semantic.neutrals_600};
    }

    &:focus,
    &:active {
      border-color: ${({ theme }) => theme.semantic.accent_500};
    }
  }

  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        max-width: ${spacing(80)};
      `,
    )}
  `}
`;

const SearchIcon = () => (
  <svg
    data-testid="leads-search-icon"
    aria-hidden="true"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIconWrapper = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.semantic.neutrals_600};

  svg {
    width: ${({ theme: { spacing } }) => spacing(4)};
    height: ${({ theme: { spacing } }) => spacing(4)};
  }
`;

const FilterList = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
`;

const FilterLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const FilterChip = styled.button<{ $isActive: boolean }>`
  cursor: pointer;
  border-radius: ${({ theme: { spacing } }) => spacing(5)};
  border: 0.0625rem solid
    ${({ theme, $isActive }) => ($isActive ? theme.semantic.accent_500 : theme.semantic.neutrals_400)};
  background: ${({ theme, $isActive }) => ($isActive ? theme.semantic.accent_500 : 'transparent')};
  padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(4)}`};
  transition: background-color 0.2s ease-in-out;

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: ${({ theme: { spacing } }) => spacing(0.5)};
  }
`;

export const LeadsToolbar = ({ search, status, onSearchChange, onStatusChange }: LeadsToolbarProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();

  const searchPlaceholder = intl.formatMessage({ id: 'leads.search.placeholder' });
  const searchAriaLabel = intl.formatMessage({ id: 'leads.search.ariaLabel' });
  const filtersAriaLabel = intl.formatMessage({ id: 'leads.filters.ariaLabel' });

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  return (
    <ToolbarContainer>
      <SearchWrapper data-testid="leads-search">
        <TextField
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          value={search}
          onChange={handleSearchChange}
          icon={
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
          }
          iconPosition="left"
        />
      </SearchWrapper>
      <FilterList>
        <FilterLegend>{filtersAriaLabel}</FilterLegend>
        {LEADS_STATUS_FILTERS.map((filter) => {
          const isActive = filter === status;
          return (
            <FilterChip
              key={filter}
              type="button"
              $isActive={isActive}
              aria-pressed={isActive}
              data-testid={`leads-filter-${filter}`}
              onClick={() => onStatusChange(filter)}
            >
              <Typography variant="body3" color={isActive ? semantic.content_primary_button : undefined}>
                {intl.formatMessage({ id: `leads.filters.${filter}` })}
              </Typography>
            </FilterChip>
          );
        })}
      </FilterList>
    </ToolbarContainer>
  );
};

export default LeadsToolbar;
